import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { toast } from '../components/Toast.js';

const POLL_MS = 3000;

// Landlord-side inbox of visitor inquiries. The same view hosts the conversation
// panel for one selected inquiry to keep navigation lightweight.
export class LandlordInquiriesView extends Component {
  constructor(ctx) {
    super(ctx);
    this.selectedId = (ctx && ctx.params && ctx.params.id) || null;
    this.state = { loading: true, error: null, inquiries: null, current: null };
    this._timer = null;
    this._lastId = null;
    this._seen = new Set();
    this._bound = false;
    this._onVisibility = this._onVisibility.bind(this);
  }

  template() {
    if (this.state.loading) return `<section class="stack"><div class="muted">${esc(t('common.loading'))}</div></section>`;
    if (this.state.error)   return `<section class="stack"><div class="error-banner">${esc(this.state.error)}</div></section>`;
    return `
      <section class="stack">
        <h2>${esc(t('inquiries.inbox_title'))}</h2>
        ${this._inboxHtml()}
        ${this.state.current ? this._chatHtml(this.state.current) : ''}
      </section>
    `;
  }

  _inboxHtml() {
    const items = this.state.inquiries || [];
    if (items.length === 0) return `<div class="card"><div class="muted">${esc(t('inquiries.inbox_empty'))}</div></div>`;
    return `
      <div class="card stack">
        ${items.map((i) => `
          <a class="list-item" href="#/inquiries/${esc(i.id)}">
            <div class="stack-2 grow">
              <strong>${esc(i.visitor_name)} <span class="faint">· ${esc(i.visitor_email)}</span></strong>
              <span class="faint">${esc(t('inquiries.about'))}: ${esc(i.property_name)} · ${esc(i.last_message_at || i.created_at || '')}</span>
            </div>
            ${i.unread_count > 0 ? `<span class="badge badge-warning">${esc(String(i.unread_count))}</span>` : ''}
          </a>`).join('')}
      </div>
    `;
  }

  _chatHtml(inq) {
    return `
      <div class="card stack" data-chat>
        <div class="row-between">
          <div class="row" style="gap:var(--space-2)">${icon('send', { size: 'sm' })}<strong>${esc(inq.visitor_name)}</strong></div>
          <span class="faint">${esc(inq.visitor_email)}${inq.visitor_phone ? ' · ' + esc(inq.visitor_phone) : ''}</span>
        </div>
        <div data-chat-list class="stack-2" style="max-height:420px;overflow-y:auto;padding:var(--space-2);background:var(--color-bg);border:1px solid var(--color-border);border-radius:var(--radius-md)"></div>
        <form class="row" style="gap:var(--space-2)" data-chat-form>
          <input class="input grow" type="text" data-chat-input maxlength="4000" placeholder="${esc(t('inquiries.chat.placeholder'))}" autocomplete="off" />
          <button class="btn btn-primary" type="submit" data-chat-send>
            ${icon('send', { size: 'sm' })}<span>${esc(t('inquiries.chat.send'))}</span>
          </button>
        </form>
      </div>
    `;
  }

  mount() {
    if (this.state.loading) { this._load(); return; }
    if (this.state.current) {
      const form = this.el.querySelector('[data-chat-form]');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this._send(); });
      this._lastId = null;
      this._seen = new Set();
      this._poll(true);
      if (!this._timer) this._timer = setInterval(() => this._poll(), POLL_MS);
      if (!this._bound) {
        document.addEventListener('visibilitychange', this._onVisibility);
        this._bound = true;
      }
    }
  }

  unmount() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
    if (this._bound) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._bound = false;
    }
  }

  _onVisibility() {
    if (document.hidden) {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    } else if (!this._timer && this.state.current) {
      this._poll();
      this._timer = setInterval(() => this._poll(), POLL_MS);
    }
  }

  async _load() {
    try {
      const res = await api.get('/inquiries');
      const inquiries = (res && res.data) || [];
      let current = null;
      if (this.selectedId) {
        try { current = await api.get(`/inquiries/${encodeURIComponent(this.selectedId)}`); } catch (_) {}
      }
      this.setState({ loading: false, inquiries, current });
    } catch (e) {
      this.setState({ loading: false, error: e instanceof ApiError ? e.message : t('common.failed_to_load') });
    }
  }

  async _poll(initial = false) {
    if (document.hidden || !this.state.current) return;
    try {
      const id = this.state.current.id;
      const qs = this._lastId ? `?since=${encodeURIComponent(this._lastId)}` : '';
      const res = await api.get(`/inquiries/${encodeURIComponent(id)}/messages${qs}`);
      const items = Array.isArray(res.data) ? res.data : [];
      if (items.length > 0) this._append(items, initial);
    } catch (_) { /* ignore */ }
  }

  _append(items, initial) {
    const list = this.el.querySelector('[data-chat-list]');
    if (!list) return;
    const wasNearBottom = (list.scrollHeight - list.scrollTop - list.clientHeight) < 80;
    let appended = 0;
    for (const m of items) {
      if (this._seen.has(m.id)) continue;
      this._seen.add(m.id);
      this._lastId = m.id;
      const mine = m.sender_kind === 'OWNER';
      const who = mine ? t('inquiries.you') : (this.state.current.visitor_name || t('inquiries.from'));
      const row = document.createElement('div');
      row.className = 'chat-msg';
      row.style.cssText = 'display:flex;flex-direction:column;align-items:' + (mine ? 'flex-end' : 'flex-start') + ';gap:2px';
      const bubbleStyle = mine
        ? 'background:var(--color-primary-soft);color:var(--color-primary);border:1px solid var(--color-primary-soft)'
        : 'background:var(--color-bg-card);color:var(--color-text);border:1px solid var(--color-border)';
      row.innerHTML = `
        <div class="faint" style="font-size:0.75rem">${esc(who)} · ${esc(m.created_at)}</div>
        <div class="chat-bubble" style="max-width:80%;padding:6px 10px;border-radius:var(--radius-md);${bubbleStyle};white-space:pre-wrap;word-wrap:break-word">${esc(m.body)}</div>`;
      list.appendChild(row);
      appended++;
    }
    if (initial || wasNearBottom) list.scrollTop = list.scrollHeight;
  }

  async _send() {
    const input = this.el.querySelector('[data-chat-input]');
    const btn = this.el.querySelector('[data-chat-send]');
    if (!input || !this.state.current) return;
    const body = (input.value || '').trim();
    if (!body) return;
    input.disabled = true;
    if (btn) btn.disabled = true;
    try {
      const msg = await api.post(`/inquiries/${encodeURIComponent(this.state.current.id)}/messages`, { body });
      input.value = '';
      if (msg && msg.id) this._append([msg], false);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('inquiries.errors.send'));
    } finally {
      input.disabled = false;
      if (btn) btn.disabled = false;
      input.focus();
    }
  }
}
